import { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
} from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import { CloudUpload } from "@mui/icons-material";

const MarkAttendanceModal = ({ open, onClose, onSubmit, initialData = {}, loading = false }) => {
  const [formData, setFormData] = useState({
    attendanceTime: initialData.attendanceTime || new Date().toISOString(),
    topicCovered: initialData.topicCovered || "",
    homeWork: initialData.homeWork || null,
    voiceNote: initialData.voiceNote || null,
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field, file) => {
    if (file) {
      setFormData((prev) => ({ ...prev, [field]: file }));
    }
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({
      attendanceTime: new Date().toISOString(),
      topicCovered: "",
      homeWork: null,
      voiceNote: null,
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          py: 2.5,
        }}
      >
        <ArgonTypography variant="h5" color="white" fontWeight="bold">
          Mark Attendance
        </ArgonTypography>
        <ArgonTypography variant="caption" color="white" sx={{ opacity: 0.9 }}>
          Record session details and upload materials
        </ArgonTypography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <ArgonBox display="flex" flexDirection="column" gap={3}>
          {/* Attendance Time */}
          <ArgonBox>
            <ArgonTypography
              variant="button"
              fontWeight="medium"
              color="text"
              sx={{ mb: 1, display: "block" }}
            >
              Date & Time *
            </ArgonTypography>
            <TextField
              fullWidth
              type="datetime-local"
              value={formData.attendanceTime.slice(0, 16)}
              onChange={(e) =>
                handleChange("attendanceTime", new Date(e.target.value).toISOString())
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            />
          </ArgonBox>

          {/* Topic Covered */}
          <ArgonBox>
            <ArgonTypography
              variant="button"
              fontWeight="medium"
              color="text"
              sx={{ mb: 1, display: "block" }}
            >
              Topics Covered *
            </ArgonTypography>
            <ArgonInput
              fullWidth
              multiline
              rows={4}
              placeholder="Enter topics discussed in this session..."
              value={formData.topicCovered}
              onChange={(e) => handleChange("topicCovered", e.target.value)}
            />
          </ArgonBox>

          {/* File Uploads */}
          <Grid container spacing={2}>
            {/* Homework Upload */}
            <Grid item xs={12} sm={6}>
              <ArgonBox>
                <ArgonTypography
                  variant="button"
                  fontWeight="medium"
                  color="text"
                  sx={{ mb: 1, display: "block" }}
                >
                  Homework
                </ArgonTypography>
                <ArgonBox
                  component="label"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #d2d6da",
                    borderRadius: 2,
                    p: 2.5,
                    cursor: "pointer",
                    transition: "all 0.3s",
                    "&:hover": {
                      borderColor: "#5e72e4",
                      background: "#f8f9fe",
                    },
                  }}
                >
                  <CloudUpload sx={{ fontSize: 32, color: "#94a3b8", mb: 1 }} />
                  <ArgonTypography variant="caption" color="text" textAlign="center">
                    {formData.homeWork ? formData.homeWork.name : "Upload PDF/Image"}
                  </ArgonTypography>
                  <input
                    type="file"
                    hidden
                    accept="image/*,.pdf"
                    onChange={(e) => handleFileChange("homeWork", e.target.files[0])}
                  />
                </ArgonBox>
              </ArgonBox>
            </Grid>

            {/* Voice Note Upload */}
            <Grid item xs={12} sm={6}>
              <ArgonBox>
                <ArgonTypography
                  variant="button"
                  fontWeight="medium"
                  color="text"
                  sx={{ mb: 1, display: "block" }}
                >
                  Voice Note
                </ArgonTypography>
                <ArgonBox
                  component="label"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #d2d6da",
                    borderRadius: 2,
                    p: 2.5,
                    cursor: "pointer",
                    transition: "all 0.3s",
                    "&:hover": {
                      borderColor: "#5e72e4",
                      background: "#f8f9fe",
                    },
                  }}
                >
                  <CloudUpload sx={{ fontSize: 32, color: "#94a3b8", mb: 1 }} />
                  <ArgonTypography variant="caption" color="text" textAlign="center">
                    {formData.voiceNote ? formData.voiceNote.name : "Upload Audio"}
                  </ArgonTypography>
                  <input
                    type="file"
                    hidden
                    accept="audio/*"
                    onChange={(e) => handleFileChange("voiceNote", e.target.files[0])}
                  />
                </ArgonBox>
              </ArgonBox>
            </Grid>
          </Grid>
        </ArgonBox>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
        <ArgonButton
          onClick={handleClose}
          variant="outlined"
          color="secondary"
          disabled={loading}
          fullWidth
        >
          Cancel
        </ArgonButton>
        <ArgonButton
          variant="gradient"
          color="info"
          onClick={handleSubmit}
          disabled={loading || !formData.topicCovered.trim()}
          fullWidth
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Submit Attendance"}
        </ArgonButton>
      </DialogActions>
    </Dialog>
  );
};

MarkAttendanceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  loading: PropTypes.bool,
};

export default MarkAttendanceModal;
